import { Grid } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { isMobilePhone, isDate } from 'validator';

// Our components
import Alert from 'components/Alert/index';
import Loader from 'components/Loader';
import { PrimaryButton } from 'components/Button/Button';
import ReviewProfileForm from 'components/Forms/profileRelated/ReviewProfileForm';
import PhoneNumberInput from 'components/Input/PhoneNumberInput';
import ProfileAvatar from 'components/Avatar/ProfileAvatar';

// Our hooks
import useGetProfile from 'hooks/useGetProfile';
import useMutateAddProfileData from 'hooks/useMutateUpdateProfileData';

// Our Query Keys
import { ERROR_MESSAGE_DATA, SUCCESS_MESSAGE_DATA } from 'shared/query-keys';

// Utils
import { isSubmissionReady, dataIsValid } from 'shared/utils';
import getNonNullValues from 'shared/utils/clientOnboarding/getNonNullValues';
import convertDateToDatePickerFormat from 'shared/utils/formatting/convertDateToDatePickerFormat';
import convertDateToEndpointFormat from 'shared/utils/formatting/convertDateToEndpointFormat';
import formatPhoneNumber from 'shared/utils/formatting/formatPhoneNumber';

// Constants
import PhoneNumberWhitelist from 'shared/phone-number-whitelist.json';

// helper function select userProfile related fields
const selectOnlyRequiredFields = ({
	address,
	citizenshipStatus,
	creditScore,
	dob,
	employmentStatus,
	employer,
	firstName,
	highestDegree,
	lastName,
	mfiCreditScore,
	monthlyRent,
	phoneNumber,
	veteran,
	role
}) => {
	const userProfileDataWithoutNullValues = getNonNullValues({
		address,
		citizenshipStatus,
		creditScore,
		dob,
		employmentStatus,
		employer,
		firstName,
		highestDegree,
		lastName,
		mfiCreditScore,
		monthlyRent,
		phoneNumber,
		veteran,
		role
	});

	return userProfileDataWithoutNullValues;
};

function SettingsForm() {
	const queryClient = useQueryClient();

	const { isLoading, isSuccess, data } = useGetProfile(
		selectOnlyRequiredFields
	);

	const updateProfile = useMutateAddProfileData();

	// true for Advisors.
	const isAdvisor = useMemo(() => data?.role === 'ADVISOR', [data]);

	const {
		isLoading: isUpdateProfileLoading,
		isSuccess: isUpdateProfileSuccess,
		isError: isUpdateProfileError
	} = updateProfile;

	// Required fields
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [dob, setDOB] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');

	// Other Personal Fields
	const [isVeteran, setIsVeteran] = useState('');
	const [educationRating, setEducationRating] = useState('');

	// Optional fields
	const [creditScore, setCreditScore] = useState('');
	const [monthlyRentPayment, setMonthlyRentPayment] = useState('');
	const [employer, setEmployer] = useState('');

	// separate
	const [disableAutoComplete, setDisableAutoComplete] = useState(false);

	const requiredFormData = [firstName, lastName, phoneNumber];

	const isValidDate = useMemo(() => {
		const isStringDate = typeof dob === 'string';
		const isDateType = dob instanceof Date;
		if (isStringDate && dob !== '') {
			const isDateCheckWithConversion = isDate(new Date(dob));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(dob);
		}
		// if dob is null this returns false;
		return false;
	}, [dob]);

	const isValidPhoneNumber = useMemo(() => {
		if (PhoneNumberWhitelist.includes(phoneNumber)) {
			return true;
		}
		return isMobilePhone(phoneNumber, 'en-US');
	}, [phoneNumber]);

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(requiredFormData) &&
			phoneNumber !== '' &&
			isValidPhoneNumber &&
			isValidDate,
		[...requiredFormData, phoneNumber, dob]
	);

	useEffect(() => {
		if (isSuccess) {
			setDisableAutoComplete(true);
			const WANTED_KEY_SET_MAPPING = {
				dob: setDOB,
				employer: setEmployer,
				firstName: setFirstName,
				highestDegree: setEducationRating,
				lastName: setLastName,
				monthlyRent: setMonthlyRentPayment,
				phoneNumber: setPhoneNumber,
				veteran: setIsVeteran
			};

			const userProfileFields = Object.keys(data);

			try {
				// manual pre-populate
				const hasCreditScore = dataIsValid(data?.creditScore);
				const hasMfiCreditScore = dataIsValid(data?.mfiCreditScore);

				const currentCreditScore = data?.creditScore;
				const currentMfiCreditScore = data?.mfiCreditScore;
				if (hasCreditScore && hasMfiCreditScore) {
					// take any nonZero Value
					if (currentCreditScore > 0) {
						setCreditScore(currentCreditScore);
					} else {
						setCreditScore(currentMfiCreditScore);
					}
				} else if (hasCreditScore) {
					// if we hit this block hasMfiCreditScore is false
					setCreditScore(currentCreditScore);
				} else {
					// if we hit this block hasCreditScore is false
					setCreditScore(currentMfiCreditScore);
				}

				// dynamic pre-populate
				userProfileFields.forEach((userProfileField) => {
					// skip these fields right off the bat.
					if (
						userProfileField === 'role' ||
						userProfileField === 'creditScore' ||
						userProfileField === 'mfiCreditScore'
					)
						return;
					const currentData = data[userProfileField];
					const setUpdater = WANTED_KEY_SET_MAPPING[userProfileField];

					if (userProfileField === 'address') {
						const addressRelatedFieldsWithoutNullValues =
							getNonNullValues(currentData);

						const addressRelatedFields = Object.keys(
							addressRelatedFieldsWithoutNullValues
						);
						const addressFieldsToIgnore = new Set([
							'addressId',
							'marketValue',
							'assedValue'
						]);
						const requiredAddressRelatedFields =
							addressRelatedFields.filter(
								(fieldName) =>
									!addressFieldsToIgnore.has(fieldName)
							);

						requiredAddressRelatedFields.forEach(
							(addressRelatedField) => {
								const currentAddressRelatedData =
									currentData[addressRelatedField];
								const addressRelatedUpdater =
									WANTED_KEY_SET_MAPPING[addressRelatedField];
								addressRelatedUpdater(
									currentAddressRelatedData
								);
							}
						);
						return;
					}

					if (userProfileField === 'phoneNumber') {
						if (PhoneNumberWhitelist.includes(currentData)) {
							setUpdater(currentData);
							return;
						}

						const formattedPhoneNumber =
							formatPhoneNumber(currentData);
						setUpdater(formattedPhoneNumber);
						return;
					}
					if (userProfileField === 'monthlyRent') {
						setUpdater(`${currentData}`);
						return;
					}
					if (userProfileField === 'veteran') {
						if (currentData) {
							setUpdater('Yes');
						} else {
							setUpdater('No');
						}
						return;
					}

					if (userProfileField === 'dob') {
						const convertedDate =
							convertDateToDatePickerFormat(currentData);

						setUpdater(convertedDate);
						return;
					}

					// Advisors have a mfiCreditScore in their profile.
					// However they currently never go through MFI and thus their mfiCreditScore value is always 0
					// This results in a bug where mfiCreditScore overwrites creditScore saved for Advisors
					// BE confirmed mfiCreditScore takes precendence over creditScore and thus for clients this overwrite is intended.
					if (userProfileField === 'mfiCreditScore') {
						if (isAdvisor) return;
					}

					setUpdater(currentData);
				});
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e);
			}
		}
	}, [data]);

	const handleUpdateProfileClick = () => {
		const filteredPhoneNumber = phoneNumber?.replaceAll('-', '');
		const formattedCreditScore = Number.parseInt(creditScore, 10);
		const formattedRent = Number.parseInt(monthlyRentPayment, 10);
		const formattedIsVeteran = isVeteran === 'Yes';
		const dobChanged = typeof dob !== 'string';

		const userDataForProfile = {
			creditScore: formattedCreditScore,
			highestDegree: educationRating,
			employer,
			firstName,
			isVeteran: formattedIsVeteran,
			lastName,
			monthlyRent: formattedRent,
			phoneNumber: filteredPhoneNumber
		};

		if (dobChanged) {
			const formattedDob = convertDateToEndpointFormat(dob);
			userDataForProfile.dob = formattedDob;
		}

		updateProfile.mutate(userDataForProfile, {
			onSuccess: () => {
				queryClient.setQueryData(
					SUCCESS_MESSAGE_DATA,
					'Your profile was updated!'
				);
			},
			onError: (e) => {
				queryClient.setQueryData(ERROR_MESSAGE_DATA, e.message);
			}
		});
	};

	if (isLoading) return <Loader size={50} />;

	if (isSuccess) {
		return (
			<>
				<Grid container item xs={12} sx={{ gap: 10, marginBottom: 4 }}>
					<ProfileAvatar isStatic={false} />
				</Grid>

				{isUpdateProfileSuccess && <Alert variant="success" />}
				{isUpdateProfileError && <Alert variant="error" />}

				<Grid
					component="form"
					container
					item
					spacing={2}
					noValidate
					autoComplete="off"
				>
					<ReviewProfileForm
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
						withDisabled={false}
						firstName={firstName}
						setFirstName={setFirstName}
						lastName={lastName}
						setLastName={setLastName}
						dob={dob}
						setDOB={setDOB}
						isVeteran={isVeteran}
						setIsVeteran={setIsVeteran}
						educationRating={educationRating}
						setEducationRating={setEducationRating}
						creditScore={creditScore}
						setCreditScore={setCreditScore}
						monthlyRentPayment={monthlyRentPayment}
						setMonthlyRentPayment={setMonthlyRentPayment}
						employer={employer}
						setEmployer={setEmployer}
					/>

					<Grid item xs={6}>
						<PhoneNumberInput
							phoneNumber={phoneNumber}
							onChange={setPhoneNumber}
							isValidPhoneNumber={isValidPhoneNumber}
						/>
					</Grid>
				</Grid>

				{isUpdateProfileLoading && (
					<Loader boxSX={{ height: 32, marginTop: 4 }} />
				)}
				{!isUpdateProfileLoading && (
					<PrimaryButton
						disabled={!isFormReady}
						sx={{ marginTop: 4, height: 32 }}
						onClick={handleUpdateProfileClick}
					>
						Save
					</PrimaryButton>
				)}
			</>
		);
	}
}

export default SettingsForm;
